import { isElement, isText, isDocument } from '../utils/dom';
import { forEach } from '../utils';

export const IGNORE_ATTRIBUTE = 'data-ark-marker';

export function escape(str: string) {
  return str.split(/([\^\[\]\(\),;=])/g).map((x, index) => {
    return (index % 2) === 1 ? `^${x}` : x;
  }).join('');
}

export function unescape(str: string) {
  let result = '';
  let index = 0;
  for (const x of str.split(/\^(.)/g)) {
    if ((index % 2) === 1) {
      if (x.match(/[\^\[\]\(\),;=]/) !== null) {
        result += x;
      } else {
        return;
      }
    } else {
      result += x;
    }
    index += 1;
  }
  return result;
}

export function nodeToStep(node: Node): number {
  const prev = node.previousSibling;
  const offset = isElement(node) && !node.hasAttribute(IGNORE_ATTRIBUTE) ? 1 : 0;
  if (prev === null) {
    return 1 + offset;
  }
  if (isElement(prev) && !prev.hasAttribute(IGNORE_ATTRIBUTE)) {
    return nodeToStep(prev) + 1 + offset;
  }
  return nodeToStep(prev) + offset;
}

function textLengthOfIgnoreElement(element: Element): number {
  let result = 0;
  forEach(element.childNodes, (node) => {
    if (isText(node)) {
      result += node.length;
    } else if (isElement(node)) {
      result += textLengthOfIgnoreElement(node);
    }
  });
  return result;
}

export function nodeToOffset(node: Node): number {
  const prev = node.previousSibling;
  if (prev === null) {
    const parent = node.parentNode;
    if (parent === null || !isElement(parent)) {
      return 0;
    }
    if (parent.hasAttribute(IGNORE_ATTRIBUTE)) {
      return nodeToOffset(parent);
    }
    return 0;
  }
  if (isText(prev)) {
    return nodeToOffset(prev) + prev.length;
  }
  if (isElement(prev)) {
    if (prev.hasAttribute(IGNORE_ATTRIBUTE)) {
      return nodeToOffset(prev) + textLengthOfIgnoreElement(prev);
    }
    return 0;
  }
  return nodeToOffset(prev);
}

export interface DOMNodePosition {
  node: Node;
}

export interface DOMTextPosition {
  text: Text;
  offset: number;
}

export interface DOMEmptyPosition {
  prev: Node | null;
  next: Node | null;
}

export type DOMPosition = DOMNodePosition | DOMTextPosition | DOMEmptyPosition;

export function isNodePosition(position: DOMPosition): position is DOMNodePosition {
  return 'node' in position;
}

export function isTextPosition(position: DOMPosition): position is DOMTextPosition {
  return 'text' in position;
}

export function isEmptyPosition(position: DOMPosition): position is DOMEmptyPosition {
  return 'prev' in position;
}

export function getOwnerDocument(position: DOMPosition) {
  if (isNodePosition(position)) {
    return isDocument(position.node) ? position.node : position.node.ownerDocument;
  }
  if (isTextPosition(position)) {
    return position.text.ownerDocument;
  }
  return position.prev !== null ? position.prev.ownerDocument : position.next!.ownerDocument;
}

function getFirstInnerNode(node: Node): Node | undefined {
  for (
    let current: Node | null = node.firstChild;
    current !== null;
    current = current.nextSibling
  ) {
    if (!isElement(current)) {
      return current;
    }
    if (!current.hasAttribute(IGNORE_ATTRIBUTE)) {
      return current;
    }
    const result = getFirstInnerNode(current);
    if (result !== undefined) {
      return result;
    }
  }
}

function getFirstInnerText(node: Node): Text | null | undefined {
  for (
    let child: Node | null = node.firstChild;
    child !== null;
    child = child.nextSibling
  ) {
    if (isText(child)) {
      return child;
    }
    if (isElement(child)) {
      if (child.hasAttribute(IGNORE_ATTRIBUTE)) {
        const inner = getFirstInnerText(child);
        if (inner === undefined) {
          continue;
        }
        return inner;
      }
      return null;
    }
  }
}

function getNextNode(node: Node): Node | undefined {
  const { nextSibling } = node;
  if (nextSibling !== null) {
    if (!isElement(nextSibling)) {
      return nextSibling;
    }
    if (!nextSibling.hasAttribute(IGNORE_ATTRIBUTE)) {
      return nextSibling;
    }
    const result = getFirstInnerNode(nextSibling);
    if (result !== undefined) {
      return result;
    }
    return getNextNode(nextSibling);
  }
  const { parentNode } = node;
  if (parentNode === null || !isElement(parentNode) || !parentNode.hasAttribute(IGNORE_ATTRIBUTE)) {
    return;
  }
  return getNextNode(parentNode);
}

function getNextText(node: Node): Text | undefined {
  let next = getNextNode(node);
  if (next === undefined) {
    return;
  }
  while (next !== undefined && !isText(next)) {
    if (isElement(next)) {
      if (!next.hasAttribute(IGNORE_ATTRIBUTE)) {
        return;
      }
      const inner = getFirstInnerText(next);
      if (inner === null) {
        return;
      }
      if (inner !== undefined) {
        return inner;
      }
    }
    next = getNextNode(next);
  }
  return next;
}

export function offsetToPoint(node: Node, offset: number): DOMTextPosition | undefined {
  for (let current: Node | undefined = node, off = offset;
      current !== undefined;
      current = getNextText(current)) {
    if (isText(current)) {
      if (off <= current.length) {
        return { text: current, offset: off };
      }
      off -= current.length;
      continue;
    }
    if (isElement(current)) {
      if (current.hasAttribute(IGNORE_ATTRIBUTE)) {
        const inner = getFirstInnerText(current);
        if (inner === null) {
          return;
        }
        if (inner === undefined) {
          continue;
        }
        if (off <= inner.length) {
          return { text: inner, offset: off };
        }
        off -= inner.length;
        current = inner;
        continue;
      }
      return;
    }
  }
}

export function stepToNode(node: Node, step: number):
    DOMNodePosition | DOMEmptyPosition | undefined {
  if (!isElement(node) || node.hasAttribute(IGNORE_ATTRIBUTE)) {
    if (step === 1) {
      return { node };
    }
    const next = getNextNode(node);
    if (next !== undefined) {
      return stepToNode(next, step);
    }
    return;
  }
  if (step === 1) {
    return { prev: node.previousSibling, next: node };
  }
  if (step === 2) {
    return { node };
  }
  const next = getNextNode(node);
  if (next === undefined) {
    if (step === 3) {
      return { prev: node, next: null };
    }
    return;
  }
  return stepToNode(next, step - 2);
}
